/**
 * Packaging Processing Activity Grid Component
 * List view for Processing Activities (packaging) — story 36997
 * Structural base: packaging-pcf/material-taxonomies/material-grid (memoised version), not the
 * food processing-grid, per the FE spec's Decisions §5.
 */

import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Input, Breadcrumbs, Button } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { GET_ALL_PACKAGING_PROCESSES } from '../../../../query/packaging-processing-activity'
import { EXPORT_PACKAGING_PROCESSING_QUERY } from '../../../../query/export-packaging-processing'
import { IPackagingProcessingGridItem } from './types'
import { getPackagingProcessingVars } from './helpers'
import { filterEnums, getTableData } from '../../../../shared/gridFunctions'
import Table from '../../../../components/Table'
import { ExportGraphqlButton } from '../../../../components/ExportGraphqlButton'
import { EXPORT_MAX_ROW_COUNT, ROUTES } from '../../../../const'

// @ts-ignore - SCSS module import
import './index.scss'

const PackagingProcessingGrid: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IPackagingProcessingGridItem[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [packagingProcessingVars, setPackagingProcessingVars] = useState<any>(
    getPackagingProcessingVars
  )
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, refetch } = useQuery(GET_ALL_PACKAGING_PROCESSES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getPackagingProcessingVars,
    },
  })

  const anyRefetch = useCallback(
    (variables?: any): Promise<any> => {
      return refetch(variables)
    },
    [refetch]
  )

  const setCursors = useCallback(() => {
    if (data?.packagingProcessingActivities?.pageInfo) {
      setEndCursor(data.packagingProcessingActivities.pageInfo.endCursor)
      setStartCursor(data.packagingProcessingActivities.pageInfo.startCursor)
    }
  }, [data])

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    // Refetch on mount/data-change so navigating back to the grid shows fresh data instead of a
    // stale Apollo cache-first result (repo convention, see CLAUDE.md "Grid remount refetch").
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      ROUTES.packagingProcessingItem,
      'packagingProcessingActivities',
      'processingActivity'
    )
  }, [data, loading, refetch, setCursors])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setPackagingProcessingVars({
          ...filterEnums(tableSearch, getPackagingProcessingVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getPackagingProcessingVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading, data, anyRefetch])

  // EXPORT_PACKAGING_PROCESSING_QUERY only declares $first/$after/$where/$order (mirrors food's
  // export-processing.ts exactly) — str/secondaryDataReferenceSourceType/state aren't operation
  // variables on this query, so passing them here silently did nothing (the bug this replaced).
  // where: null matches food's export pattern: export is a full unfiltered dump, not a mirror of
  // the grid's active search; the packaging root field is already server-side type-scoped (D6),
  // so there's no type filter to thread through here either.
  const exportPayload = useMemo(
    () => ({
      operationName: 'PackagingProcessingActivities',
      query: EXPORT_PACKAGING_PROCESSING_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  const handleAddNew = useCallback(() => {
    navigate(`${ROUTES.packagingProcessingItem}?itemId=new`)
  }, [navigate])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDebouncedTableSearch(e.target.value)
  }, [])

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[{ path: ROUTES.packagingProcessing, title: 'Processing Activity Table' }]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Processing Activity</h2>
        <div className="packaging-actions items-center">
          <Input onChange={handleSearchChange} iconType="search" placeholder="Search" />
          <Button variant="primary" onClick={handleAddNew}>
            Add Process
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={packagingProcessingVars || getPackagingProcessingVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default PackagingProcessingGrid
